import { Button } from "@/components/ui/button";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { Plus, Shield, Crown, Eye, Settings } from "lucide-react";

interface EmptyStateRolesProps {
  onCreateRole: () => void;
}

export default function EmptyStateRoles({ onCreateRole }: EmptyStateRolesProps) {
  const roleTemplates = [
    {
      name: "Super Admin",
      description: "Full system access and control",
      icon: <Crown className="h-5 w-5 text-amber-600" />,
      color: "bg-amber-50 border-amber-200",
    },
    {
      name: "Device Admin", 
      description: "Manage devices and configurations",
      icon: <Settings className="h-5 w-5 text-blue-600" />,
      color: "bg-blue-50 border-blue-200",
    },
    {
      name: "Operator",
      description: "Day-to-day operational tasks",
      icon: <Shield className="h-5 w-5 text-green-600" />,
      color: "bg-green-50 border-green-200",
    },
    {
      name: "Viewer",
      description: "Read-only access to resources",
      icon: <Eye className="h-5 w-5 text-purple-600" />,
      color: "bg-purple-50 border-purple-200",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Empty state card */}
      <GlassCard variant="subtle" className="border-dashed border-2 border-muted-foreground/25">
        <GlassCardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <Shield className="h-10 w-10 text-muted-foreground" />
          </div>
          
          <h3 className="mt-6 text-lg font-semibold text-foreground">
            No custom roles created yet
          </h3>
          
          <p className="mt-2 text-sm text-muted-foreground max-w-md">
            Create custom roles to define specific permissions and access levels for your team members.
          </p>
          
          <Button onClick={onCreateRole} className="mt-6 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Create Custom Role</span>
          </Button>
        </GlassCardContent>
      </GlassCard>

      {/* Role template showcase */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Common Role Templates
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Here are some examples of roles you can create to organize your team's access
        </p>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {roleTemplates.map((template) => (
            <GlassCard key={template.name} variant="subtle">
              <div className={`${template.color} border rounded-2xl`}>
              <GlassCardContent className="p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 rounded-lg bg-white shadow-sm">
                    {template.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{template.name}</h4>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {template.description}
                </p>
              </GlassCardContent>
              </div>
            </GlassCard>
          ))}
        </div>
        
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={onCreateRole}>
            <Plus className="h-4 w-4 mr-2" />
            Start with a Template
          </Button>
        </div>
      </div>
    </div>
  );
}